import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DateInput from '../../components/ui/DateInput';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { ArrowLeft, ArrowRight, User, AlertCircle } from 'lucide-react';

const ClientInfo = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useTheme();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    email: '',
    cellphone: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadAssessment();
  }, [id]);

  const loadAssessment = async () => {
    try {
      const response = await api.get(`/assessments/${id}/client-info`);
      const clientData = response.data;
      setAssessment({ id, client: clientData });
      if (clientData) {
        setFormData({
          full_name: clientData.full_name || '',
          date_of_birth: clientData.date_of_birth ? new Date(clientData.date_of_birth).toISOString().split('T')[0] : '',
          email: clientData.email || '',
          cellphone: clientData.cellphone || '',
        });
      }
    } catch (error) {
      console.error('Failed to load client info:', error);
      setLoadError('Unable to load your information. Please go back and try again, or contact your advisor.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) newErrors.full_name = 'Name is required';
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.date_of_birth);
      const today = new Date();
      let calcAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) calcAge--;
      if (calcAge < 18 || calcAge > 100) newErrors.date_of_birth = 'You must be between 18 and 100 years old';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.cellphone.trim()) newErrors.cellphone = 'Cellphone number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      // Update the client record using the correct client_id
      const payload = {
        ...formData,
      };
      await api.patch(`/assessments/${id}/client-info`, payload);
      navigate(`/assessment/${id}/consent`);
    } catch (error) {
      console.error('Failed to save client info:', error);
      const msg = error.response?.data?.error || error.message || 'Failed to save your information';
      setErrors({ submit: msg });
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme.primary}20` }}>
                <User className="w-6 h-6" style={{ color: theme.primary }} />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>
                Your Information
              </h1>
            </div>
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Please verify your details below
            </p>
          </CardHeader>

          <CardContent>
            {errors.submit && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{errors.submit}</p>}
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin" style={{ color: theme.primary }}></div>
                <p className="mt-4 text-sm" style={{ color: theme.textSecondary }}>Loading your information...</p>
              </div>
            ) : loadError ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                <p className="text-sm text-red-600 dark:text-red-400 mb-4">{loadError}</p>
                <Button variant="ghost" onClick={() => navigate(-1)} className="flex items-center mx-auto">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
                </Button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  placeholder="Enter your full name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  error={errors.full_name}
                  required
                />
                <DateInput
                  label="Date of Birth"
                  value={formData.date_of_birth}
                  onChange={(iso) => setFormData({ ...formData, date_of_birth: iso })}
                  error={errors.date_of_birth}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    error={errors.email}
                    required
                  />
                </div>
                <div>
                  <Input
                    label="Cellphone Number"
                    type="tel"
                    placeholder="082 123 4567"
                    value={formData.cellphone}
                    onChange={(e) => setFormData({ ...formData, cellphone: e.target.value })}
                    error={errors.cellphone}
                    required
                  />
                </div>
              </div>

              <div className="pt-4 flex justify-between">
                <Button type="button" variant="ghost" onClick={() => navigate(-1)} className="flex items-center whitespace-nowrap">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button type="submit" className="flex items-center whitespace-nowrap">
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientInfo;
